'use client';
import { Heart } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useState } from 'react';
import { Button } from '@//components/Button';
import { IconButton } from '@//components/IconButton';
import { Spinner } from '@//components/Spinner';
import useCreateVehicleSubscription from '@//data/subscriptions/vehicle/useCreateVehicleSubscription';
import useDeleteVehicleSubscription from '@//data/subscriptions/vehicle/useDeleteVehicleSubscription';
import useVehicleSubscription from '@//data/subscriptions/vehicle/useVehicleSubscription';
import { useAuth } from '@//providers/AuthProvider';

const SignInModal = lazy(() => import('../SignInModal'));

import { useLegacyTrackSave } from '@//data/tracking/useLegacyTrackSave';
import { createSubscriptionVariables, type SubscriptionItem } from './utils';

export const vehicleSubscriptionHeartFilledColor = 'var(--color-icon-brand)';

interface VehicleSubscriptionButtonProps {
  item: SubscriptionItem;
}

const VehicleSubscriptionButton = ({ item }: VehicleSubscriptionButtonProps) => {
  const tCommon = useTranslations('other.common');
  const tSignInModal = useTranslations('components.signInModal.saveVehicle');
  const { initialized } = useAuth();
  const [signInModal, setSignInModal] = useState(false);
  const { trackLegacySave } = useLegacyTrackSave();

  const subscriptionQuery = useVehicleSubscription(item.vehicle._id);
  const subscription = subscriptionQuery.data?.response?.find(
    (sub) => sub.advertisementId === item.vehicle._id,
  );
  const mutationDelete = useDeleteVehicleSubscription();
  const unsubscribe = mutationDelete.mutate;
  const mutationSubscribe = useCreateVehicleSubscription();

  const loading = mutationSubscribe.isPending || mutationDelete.isPending;

  const onSubscribe = useCallback(() => {
    trackLegacySave({ vehicle: item.vehicle, pageType: 'product page' });
    mutationSubscribe.mutate(createSubscriptionVariables(item));
  }, [mutationSubscribe, item, trackLegacySave]);

  const onToggleSubscription = useCallback(() => {
    if (subscription) {
      unsubscribe({ id: subscription.id });
    } else {
      onSubscribe();
    }
  }, [subscription, unsubscribe, onSubscribe]);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!initialized) {
        setSignInModal(true);
        return;
      }
      onToggleSubscription();
    },
    [initialized, onToggleSubscription],
  );

  return (
    <>
      <Suspense fallback={null}>
        <SignInModal
          title={tSignInModal('title')}
          isOpen={signInModal}
          description={tSignInModal('description')}
          interactionType={
            item.type === 'ElasticVehicle' ? 'Save object' : 'Save object in search result'
          }
          onClose={() => setSignInModal(false)}
        />
      </Suspense>
      {item.type === 'ElasticVehicle' ? (
        <Button
          title={subscription ? tCommon('unSave.title.vehicle') : tCommon('save.title.vehicle')}
          onClick={onClick}
          size="md"
          variant="no-bg"
          fullWidth
          data-testid="vehicle-subscription-button"
        >
          {loading ? (
            <Spinner size="text" />
          ) : (
            <Heart
              stroke={subscription ? vehicleSubscriptionHeartFilledColor : 'currentColor'}
              fill={subscription ? vehicleSubscriptionHeartFilledColor : 'none'}
              aria-hidden="true"
            />
          )}
          {subscription ? tCommon('unSave.label') : tCommon('save.label')}
        </Button>
      ) : (
        <IconButton
          label={subscription ? tCommon('unSave.label') : tCommon('save.label')}
          title={subscription ? tCommon('unSave.title.vehicle') : tCommon('save.title.vehicle')}
          onClick={onClick}
          size="md"
          variant="inverted"
        >
          {loading ? (
            <Spinner size="text" />
          ) : (
            <Heart
              stroke={subscription ? vehicleSubscriptionHeartFilledColor : 'currentColor'}
              fill={subscription ? vehicleSubscriptionHeartFilledColor : 'none'}
              aria-hidden="true"
            />
          )}
        </IconButton>
      )}
    </>
  );
};

export default VehicleSubscriptionButton;
